import { Field } from '@/components/ui/field';
import { invalidateBrandSpaceProgramOptions } from '@/hooks/queries/use-brand-space-program-options-query';
import { updateProgram } from '@/services/affiliate-programs-service';
import { Input, Textarea, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const isValidUrl = (value: string) => z.string().url().safeParse(value).success;

const programInformationSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(3, t('programs.validation.nameMinLength')),
    description: z.string().nullable().optional(),
    // Empty input clears the field (transformed to undefined, sent as null).
    destinationUrl: z
      .string()
      .nullable()
      .optional()
      .transform((value) => value?.trim() || undefined)
      .refine((value) => value === undefined || isValidUrl(value), {
        message: t('programs.validation.destinationUrlInvalid'),
      }),
  });

export type ProgramInformationFormData = {
  name: string;
  description?: string | null;
  destinationUrl?: string | null;
};

interface ProgramInformationFormProps {
  programId: string;
  defaultValues?: Partial<ProgramInformationFormData>;
  onSuccess: () => void;
  onSubmitting: (isSubmitting: boolean) => void;
}

const ProgramInformationForm = forwardRef(
  ({ programId, defaultValues, onSuccess, onSubmitting }: ProgramInformationFormProps, ref) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const schema = useMemo(() => programInformationSchema(t), [t]);

    const methods = useForm<ProgramInformationFormData>({
      resolver: zodResolver(schema),
      defaultValues,
    });

    const handleMutation = async (data: ProgramInformationFormData) => {
      return await updateProgram(programId, {
        ...data,
        // The backend only clears destinationUrl on an explicit null - an
        // empty field must send null, not be omitted from the payload.
        destinationUrl: data.destinationUrl ?? null,
      });
    };

    const mutation = useMutation({
      mutationFn: handleMutation,
      onSuccess: () => {
        // Invalidate specific program and programs list to refresh the data
        queryClient.invalidateQueries({
          queryKey: ['program', programId],
        });
        queryClient.invalidateQueries({
          queryKey: ['programs'],
        });
        void invalidateBrandSpaceProgramOptions(queryClient);
        onSuccess(); // Close the modal
      },
      onError: (error) => {
        console.error('Failed to update program information:', error);
      },
    });

    useEffect(() => {
      onSubmitting(mutation.isPending);
    }, [onSubmitting, mutation.isPending]);

    const onSubmit = (data: ProgramInformationFormData) => {
      mutation.mutate(data);
    };

    // Expose the submit method to the parent using `ref`
    useImperativeHandle(ref, () => ({
      submit: methods.handleSubmit(onSubmit),
    }));

    return (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <VStack gap={4} align="stretch">
            <Field
              label={t('programs.modals.editInformation.programName')}
              invalid={!!methods.formState.errors.name}
            >
              <Input
                rounded="lg"
                placeholder={t('programs.modals.editInformation.programNamePlaceholder')}
                {...methods.register('name')}
              />
            </Field>
            <Field label={t('programs.modals.editInformation.description')}>
              <Textarea
                rounded="lg"
                placeholder={t('programs.modals.editInformation.descriptionPlaceholder')}
                {...methods.register('description')}
                minH="100px"
              />
            </Field>
            <Field
              label={t('programs.modals.editInformation.destinationUrl')}
              helperText={t('programs.modals.editInformation.destinationUrlHelper')}
              invalid={!!methods.formState.errors.destinationUrl}
              errorText={methods.formState.errors.destinationUrl?.message}
            >
              <Input
                variant="outline"
                rounded="lg"
                placeholder={t('programs.modals.editInformation.destinationUrlPlaceholder')}
                {...methods.register('destinationUrl')}
              />
            </Field>
          </VStack>
        </form>
      </FormProvider>
    );
  }
);

export default ProgramInformationForm;
